/**
 * WhatsApp Template Service — per-business templates from API + profile context.
 */
import axios from "axios";
import { getApiBaseUrl, getAuthHeader } from "./httpClient";

const STORAGE_KEYS = {
  billCompletion: "whatsapp_bill_completion_template",
  workStatus: "whatsapp_work_status_template",
  jobAlert: "whatsapp_job_alert_template",
  welcome: "whatsapp_welcome_template",
  enquiry: "whatsapp_enquiry_template",
  studentRegistration: "whatsapp_student_registration_template",
  billCompletionOptions: "whatsapp_bill_completion_options",
  messageOptions: "whatsapp_message_options",
  cachePrefix: "whatsapp_tenant_config_",
};

const LEGACY_DEFAULT_TEMPLATES = {
  billCompletion: `**👋 Hi Mr/Ms {name}! (హాయ్ {name} గారు!)** **💐 {businessName} ను ఎంచుకున్నందుకు ధన్యవాదాలు.**

మీ బిల్‌తో పాటు మీకు ఒక Special KNLS Coupon లభించింది. 🎁

✅ ₹150 విలువైన PVC Aadhaar Card కేవలం ₹75కే!

ఇంకా ఇతర ప్రత్యేక ఆఫర్లు కూడా మీ అకౌంట్‌లో అందుబాటులో ఉన్నాయి.

👉 మీ కూపన్లు చూడటానికి:
{dashboardUrl}

⏳ Offers పరిమిత కాలం మాత్రమే.

📞 {businessPhone}`,
  workStatus: `*Work Status — {businessName}*

*Bill No:* {billNo}
*Customer:* {customerName}
*Date:* {billDate}

*Services:*
{servicesList}

*Status:* {status}
{remarks}

📱 {businessPhone}`,
  jobAlert: `🎯 *{jobTitle}*
{jobUrl}

— {businessName}
📱 {businessPhone}`,
  welcome: `Welcome to {businessName}!

📱 {businessPhone}
📍 {businessAddress}
{servicesUrl}`,
  enquiry: `Hi {name}, thanks for contacting {businessName}.

📱 {businessPhone}
{trainingProgramsUrl}
{jobAlertsUrl}`,
  studentRegistration: `🎓 *Student Login Credentials*

*Student Name:* {studentName}
*Student ID:* {studentId}
*Phone:* {phone}
*Password:* {password}

👉 *Click here to login automatically:*
{autoLoginUrl}

Or visit: {studentLoginUrl}

📱 *Install KNLS Student App:*
{studentAppPlayStoreUrl}

_This link will automatically log you in to your student dashboard._`,
};

export const DEFAULT_MESSAGE_OPTIONS = {
  billCompletion: {
    includeCustomerName: true,
    includeServiceName: false,
    includeTotalAmount: false,
    includeWorkStatus: false,
    includeCouponMessage: false,
    includePaymentDetails: false,
    includePaymentStatus: false,
    includePaymentMethods: false,
    includeDueAmount: false,
    includeChangeAmount: false,
    includeDashboardUrl: true,
    includeServicesUrl: false,
  },
  workStatus: {
    includeBillNo: true,
    includeCustomerName: true,
    includeBillDate: true,
    includeServicesList: true,
    includeStatus: true,
    includeRemarks: true,
    includeServiceNames: true,
  },
  jobAlert: {
    includeJobTitle: true,
    includeCategory: true,
    includeOrganization: true,
    includeVacancies: true,
    includeQualification: true,
    includeLastDate: true,
    includeLocation: true,
    includeSalary: true,
    includeAgeLimit: true,
    includeJobUrl: true,
    includeBasePageUrl: true,
  },
  welcome: {
    includeServicesUrl: true,
    includeCoursesUrl: true,
    includeBaseUrl: true,
  },
  enquiry: {
    includeName: true,
    includeTrainingProgramsUrl: true,
    includeJobAlertsUrl: true,
  },
  studentRegistration: {
    includeStudentName: true,
    includeStudentId: true,
    includePhone: true,
    includePassword: true,
    includeAutoLoginUrl: true,
    includeStudentLoginUrl: true,
    includeStudentAppPlayStoreUrl: true,
  },
};

let tenantCache = {
  ownerKey: null,
  templates: null,
  billCompletionOptions: null,
  messageOptions: null,
  businessContext: {},
};

const scopedStorageKey = (base, ownerKey) =>
  ownerKey ? `${base}__${ownerKey}` : base;

const readScopedLocal = (base, ownerKey) => {
  try {
    const key = scopedStorageKey(base, ownerKey);
    return localStorage.getItem(key) || localStorage.getItem(base);
  } catch {
    return null;
  }
};

const writeScopedLocal = (base, ownerKey, value) => {
  try {
    if (ownerKey) {
      localStorage.setItem(scopedStorageKey(base, ownerKey), value);
    }
    localStorage.setItem(base, value);
  } catch (error) {
    console.warn("whatsapp localStorage save failed:", error);
  }
};

export const normalizeMessageOptions = (rawOptions = null, legacyBillOptions = null) => {
  const merged = {};
  Object.keys(DEFAULT_MESSAGE_OPTIONS).forEach((type) => {
    merged[type] = { ...DEFAULT_MESSAGE_OPTIONS[type] };
  });

  const source =
    rawOptions && typeof rawOptions === "object"
      ? rawOptions
      : legacyBillOptions && typeof legacyBillOptions === "object"
        ? { billCompletion: legacyBillOptions }
        : {};

  Object.keys(DEFAULT_MESSAGE_OPTIONS).forEach((type) => {
    if (source[type] && typeof source[type] === "object") {
      merged[type] = {
        ...merged[type],
        ...source[type],
      };
    }
  });

  // Older saved configs stored bill completion options as a flat object.
  if (source.includeCustomerName !== undefined) {
    merged.billCompletion = {
      ...merged.billCompletion,
      ...source,
    };
  }

  return merged;
};

/**
 * Load config from API (call after login / on config page).
 */
export const refreshWhatsappConfig = async () => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/whatsapp-config`, {
      headers: { ...getAuthHeader() },
    });
    if (!response.data?.success) {
      return null;
    }
    const data = response.data.data || {};
    tenantCache = {
      ownerKey: data.ownerId || "default",
      templates: data.templates || {},
      billCompletionOptions:
        data.billCompletionOptions || loadBillCompletionOptions(),
      messageOptions:
        normalizeMessageOptions(data.messageOptions, data.billCompletionOptions),
      businessContext: data.businessContext || {},
    };
    try {
      localStorage.setItem(
        `${STORAGE_KEYS.cachePrefix}${tenantCache.ownerKey}`,
        JSON.stringify(tenantCache),
      );
    } catch {
      /* ignore */
    }
    return tenantCache;
  } catch (error) {
    console.warn("refreshWhatsappConfig failed:", error.message);
    return null;
  }
};

const hydrateCacheFromLocal = () => {
  try {
    const keys = Object.keys(localStorage).filter((k) =>
      k.startsWith(STORAGE_KEYS.cachePrefix),
    );
    if (!keys.length) return false;
    const raw = localStorage.getItem(keys[keys.length - 1]);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    tenantCache = {
      ownerKey: parsed.ownerKey,
      templates: parsed.templates,
      billCompletionOptions: parsed.billCompletionOptions,
      messageOptions: normalizeMessageOptions(
        parsed.messageOptions,
        parsed.billCompletionOptions,
      ),
      businessContext: parsed.businessContext || {},
    };
    return true;
  } catch {
    return false;
  }
};

export const getBusinessContext = () => tenantCache.businessContext || {};

const resolveFrontendBaseUrl = (baseUrlOverride) => {
  if (baseUrlOverride) {
    return String(baseUrlOverride).replace(/\/$/, "");
  }
  const ctx = getBusinessContext();
  if (ctx.baseUrl) {
    return String(ctx.baseUrl).replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isDev = host === "localhost" || host === "127.0.0.1";
    if (isDev) {
      return window.location.origin;
    }
    return (
      process.env.REACT_APP_PRODUCTION_URL ||
      process.env.REACT_APP_FRONTEND_URL ||
      window.location.origin
    ).replace(/\/$/, "");
  }
  return "https://services.knls.in";
};

export const buildCustomerLoginUrl = (phone, baseUrlOverride) => {
  const cleanPhone = String(phone || "").replace(/\D/g, "").slice(-10);
  if (cleanPhone.length !== 10) {
    return "";
  }
  const base = resolveFrontendBaseUrl(baseUrlOverride);
  const defaultPassword = cleanPhone.slice(-4);
  // Auto-login then land on customer coupons wallet.
  return `${base}/user-login?phone=${cleanPhone}&password=${defaultPassword}&redirect=${encodeURIComponent("/user/coupons")}`;
};

/**
 * Shared bill-completion payload — message body comes from WhatsApp config templates.
 */
export const buildBillCompletionMessageData = ({
  name,
  phone,
  serviceName = "",
  paymentMethods = {},
  totalPaid = 0,
  dueAmount = 0,
  change = 0,
  workStatus = "completed",
  dashboardUrl,
  servicesUrl,
  baseUrl,
  couponMessage = "",
} = {}) => {
  const cleanPhone = String(phone || "").replace(/\D/g, "").slice(-10);
  const resolvedBaseUrl = resolveFrontendBaseUrl(baseUrl);
  const finalDashboardUrl =
    dashboardUrl || buildCustomerLoginUrl(cleanPhone, resolvedBaseUrl);

  return {
    name: name || "Customer",
    phone: cleanPhone,
    last4Digits: cleanPhone.slice(-4),
    serviceName,
    paymentInfo: "",
    paymentMethods,
    totalPaid,
    dueAmount,
    change,
    workStatus,
    couponMessage,
    dashboardUrl: finalDashboardUrl,
    servicesUrl: servicesUrl || `${resolvedBaseUrl}/services`,
  };
};

/**
 * Load a WhatsApp message template
 */
export const loadTemplate = (type) => {
  if (tenantCache.templates?.[type]) {
    return tenantCache.templates[type];
  }
  hydrateCacheFromLocal();
  if (tenantCache.templates?.[type]) {
    return tenantCache.templates[type];
  }
  const saved = readScopedLocal(STORAGE_KEYS[type], tenantCache.ownerKey);
  if (saved) {
    return saved;
  }
  return LEGACY_DEFAULT_TEMPLATES[type] || "";
};

export const replacePlaceholders = (template, data) => {
  let message = template;
  Object.keys(data).forEach((key) => {
    const placeholder = `{${key}}`;
    const value = data[key] != null ? String(data[key]) : "";
    message = message.replace(new RegExp(placeholder, "g"), value);
  });
  message = message.replace(/\n{3,}/g, "\n\n");
  message = message.replace(/^\s+$/gm, "");
  return message.trim();
};

export const loadBillCompletionOptions = () => {
  return loadMessageOptions().billCompletion;
};

export const loadMessageOptions = () => {
  if (tenantCache.messageOptions) {
    return normalizeMessageOptions(
      tenantCache.messageOptions,
      tenantCache.billCompletionOptions,
    );
  }

  try {
    const saved = readScopedLocal(STORAGE_KEYS.messageOptions, tenantCache.ownerKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      const normalized = normalizeMessageOptions(parsed, tenantCache.billCompletionOptions);
      tenantCache.messageOptions = normalized;
      return normalized;
    }
  } catch (error) {
    console.error("Error loading message options:", error);
  }

  try {
    const savedBill = readScopedLocal(
      STORAGE_KEYS.billCompletionOptions,
      tenantCache.ownerKey,
    );
    if (savedBill) {
      const normalized = normalizeMessageOptions(null, JSON.parse(savedBill));
      tenantCache.messageOptions = normalized;
      return normalized;
    }
  } catch (error) {
    console.error("Error loading bill completion options:", error);
  }

  return normalizeMessageOptions();
};

export const applyPaymentInfoFilters = (data, options) => {
  if (!options.includePaymentDetails) return "";

  const paymentInfoParts = [];

  if (options.includePaymentStatus) {
    if (data.paymentStatus) {
      paymentInfoParts.push(`**💳 Payment ${data.paymentStatus}:**`);
    } else {
      paymentInfoParts.push("**💳 Payment Received:**");
    }
  }

  if (options.includePaymentMethods && data.paymentMethods) {
    Object.entries(data.paymentMethods).forEach(([method, amount]) => {
      if (amount > 0 && method !== "credit") {
        const methodEmoji = method.toLowerCase().includes("cash")
          ? "💵"
          : method.toLowerCase().includes("upi")
            ? "📱"
            : method.toLowerCase().includes("card")
              ? "💳"
              : "💰";
        paymentInfoParts.push(`${methodEmoji} ${method}: ₹${amount.toFixed(2)}`);
      }
    });
  }

  if (options.includeTotalAmount && data.totalPaid !== undefined) {
    paymentInfoParts.push(`**Total Paid: ₹${data.totalPaid.toFixed(2)}**`);
  }

  if (options.includeDueAmount && data.dueAmount !== undefined && data.dueAmount > 0) {
    paymentInfoParts.push(`**Due Amount: ₹${data.dueAmount.toFixed(2)}**`);
  }

  if (options.includeChangeAmount && data.change !== undefined && data.change > 0) {
    paymentInfoParts.push(`**Change: ₹${data.change.toFixed(2)}**`);
  }

  return paymentInfoParts.length > 0 ? `${paymentInfoParts.join("\n")}\n` : "";
};

const OPTION_FIELD_MAP = {
  billCompletion: {
    includeCustomerName: ["name"],
    includeServiceName: ["serviceName"],
    includeCouponMessage: ["couponMessage"],
    includeDashboardUrl: ["dashboardUrl"],
    includeServicesUrl: ["servicesUrl"],
  },
  workStatus: {
    includeBillNo: ["billNo"],
    includeCustomerName: ["customerName"],
    includeBillDate: ["billDate"],
    includeServicesList: ["servicesList"],
    includeStatus: ["status"],
    includeRemarks: ["remarks"],
    includeServiceNames: ["serviceNames"],
  },
  jobAlert: {
    includeJobTitle: ["jobTitle"],
    includeCategory: ["category"],
    includeOrganization: ["organization"],
    includeVacancies: ["vacancies"],
    includeQualification: ["qualification"],
    includeLastDate: ["lastDate"],
    includeLocation: ["location"],
    includeSalary: ["salary"],
    includeAgeLimit: ["ageLimit"],
    includeJobUrl: ["jobUrl"],
    includeBasePageUrl: ["basePageUrl"],
  },
  welcome: {
    includeServicesUrl: ["servicesUrl"],
    includeCoursesUrl: ["coursesUrl"],
    includeBaseUrl: ["baseUrl"],
  },
  enquiry: {
    includeName: ["name"],
    includeTrainingProgramsUrl: ["trainingProgramsUrl"],
    includeJobAlertsUrl: ["jobAlertsUrl"],
  },
  studentRegistration: {
    includeStudentName: ["studentName"],
    includeStudentId: ["studentId"],
    includePhone: ["phone"],
    includePassword: ["password"],
    includeAutoLoginUrl: ["autoLoginUrl"],
    includeStudentLoginUrl: ["studentLoginUrl"],
    includeStudentAppPlayStoreUrl: ["studentAppPlayStoreUrl"],
  },
};

export const applyMessageOptions = (type, data, options = null) => {
  const allOptions = normalizeMessageOptions(options || loadMessageOptions());
  const typeOptions = allOptions[type] || {};
  const processedData = { ...data };
  const fieldMap = OPTION_FIELD_MAP[type] || {};

  Object.entries(fieldMap).forEach(([optionKey, fields]) => {
    if (typeOptions[optionKey] === false) {
      fields.forEach((field) => {
        processedData[field] = "";
      });
    }
  });

  if (type === "billCompletion") {
    if (typeOptions.includePaymentDetails) {
      if (data.paymentMethods || data.totalPaid !== undefined) {
        processedData.paymentInfo = applyPaymentInfoFilters(data, typeOptions);
      } else if (data.paymentInfo !== undefined) {
        processedData.paymentInfo = data.paymentInfo;
      }
    } else {
      processedData.paymentInfo = "";
    }

    processedData.workStatus =
      typeOptions.includeWorkStatus && data.workStatus
        ? `*Work Status:* ${data.workStatus}\n`
        : "";
  }

  return processedData;
};

export const generateMessage = (type, data) => {
  const template = loadTemplate(type);
  const ctx = getBusinessContext();
  const mergedData = { ...ctx, ...data };
  const processedData = applyMessageOptions(type, mergedData);

  return replacePlaceholders(template, processedData);
};

const PUBLIC_JOB_ALERT_TEMPLATE = `🎯 *{jobTitle}*

📋 *{category}*
🏢 Organization: {organization}
{vacancies}
{qualification}
{lastDate}
{location}
{salary}
{ageLimit}

🔗 View Details: {jobUrl}

📢 *Updates from {businessName}*
👉 {basePageUrl}

🏢 *{businessName}*
📱 {businessPhone}`;

/**
 * Public /jobalerts share text.
 * Uses the job creator's shop (AA → KNLS Computers / 9885750476),
 * never the viewer's cached WhatsApp tenant profile.
 */
export const generatePublicJobAlertMessage = (data = {}) => {
  const mergedData = {
    businessName: "KNLS Computers",
    businessPhone: "9885750476",
    ...data,
  };
  const processedData = applyMessageOptions(
    "jobAlert",
    mergedData,
    DEFAULT_MESSAGE_OPTIONS,
  );
  return replacePlaceholders(PUBLIC_JOB_ALERT_TEMPLATE, processedData);
};

export const generateBillCompletionMessage = (data) =>
  generateMessage("billCompletion", buildBillCompletionMessageData(data));

export const encodeMessageForWhatsApp = (message) => {
  try {
    const normalized = message.normalize("NFC");
    return encodeURIComponent(normalized);
  } catch (error) {
    console.error("Error encoding message for WhatsApp:", error);
    return encodeURIComponent(message);
  }
};

/** Reuse one browser tab for WhatsApp Web when desktop app is not used. */
const WHATSAPP_WINDOW_NAME = "knls_whatsapp_web";

/**
 * Open WhatsApp with desktop-first behavior:
 * 1) whatsapp:// → Windows/Mac desktop app (no admin-page navigation)
 * 2) wa.me in a named window (previous production behavior; often opens desktop on Windows)
 * 3) web.whatsapp.com in the same named window only if popup blocked
 */
const dispatchWhatsApp = ({ desktopUrl, waMeUrl, webUrl }) => {
  if (typeof window === "undefined") return;

  let handoffDetected = false;
  const markHandoff = () => {
    handoffDetected = true;
  };

  const onBlur = () => markHandoff();
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      markHandoff();
    }
  };

  window.addEventListener("blur", onBlur);
  document.addEventListener("visibilitychange", onVisibilityChange);

  const cleanup = () => {
    window.removeEventListener("blur", onBlur);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  const openBrowserFallback = () => {
    cleanup();
    if (handoffDetected) return;

    // wa.me was the previous default and correctly opened Desktop on many Windows PCs.
    const waWindow = window.open(
      waMeUrl,
      WHATSAPP_WINDOW_NAME,
      "noopener,noreferrer",
    );

    // Popup blocked: open WhatsApp Web in the same named tab (reuse if already open).
    if (!waWindow && webUrl) {
      window.open(webUrl, WHATSAPP_WINDOW_NAME, "noopener,noreferrer");
    }
  };

  // 1) Desktop app — try protocol handoff first.
  // Some Chromium builds ignore hidden anchor clicks in async callbacks.
  // location.assign is more reliable for protocol handlers on Windows.
  try {
    window.location.assign(desktopUrl);
  } catch (error) {
    console.warn("WhatsApp desktop protocol handoff failed:", error);
  }

  // 2) Browser fallback only if desktop did not take focus shortly after handoff.
  window.setTimeout(openBrowserFallback, 900);
};

export const openWhatsApp = (phone, message, countryCode = "91") => {
  if (!phone || !message) {
    console.error("Phone number and message are required");
    return;
  }
  // Keep last 10 digits so both "10-digit" and "+91..." inputs work.
  const cleanPhone = phone.replace(/\D/g, "").slice(-10);
  if (cleanPhone.length !== 10) {
    console.error("Invalid phone number");
    return;
  }
  const encodedMessage = encodeMessageForWhatsApp(message);
  const safeCountryCode = String(countryCode || "91").replace(/\D/g, "");
  const phoneWithCountry = `${safeCountryCode}${cleanPhone}`;

  dispatchWhatsApp({
    desktopUrl: `whatsapp://send?phone=${phoneWithCountry}&text=${encodedMessage}`,
    waMeUrl: `https://wa.me/${phoneWithCountry}?text=${encodedMessage}`,
    webUrl: `https://web.whatsapp.com/send?phone=${phoneWithCountry}&text=${encodedMessage}`,
  });
};

export const shareOnWhatsApp = (message) => {
  if (!message) {
    console.error("Message is required");
    return;
  }
  const encodedMessage = encodeMessageForWhatsApp(message);
  dispatchWhatsApp({
    desktopUrl: `whatsapp://send?text=${encodedMessage}`,
    waMeUrl: `https://wa.me/?text=${encodedMessage}`,
    webUrl: `https://web.whatsapp.com/send?text=${encodedMessage}`,
  });
};

const TEMPLATE_TYPE_KEYS = [
  "billCompletion",
  "workStatus",
  "jobAlert",
  "welcome",
  "enquiry",
  "studentRegistration",
];

export const saveTemplatesLocally = (
  ownerKey,
  templates,
  billCompletionOptions,
  messageOptions,
) => {
  if (templates) {
    tenantCache.templates = templates;
    tenantCache.ownerKey = ownerKey || tenantCache.ownerKey;
    TEMPLATE_TYPE_KEYS.forEach((type) => {
      if (templates[type] != null && STORAGE_KEYS[type]) {
        writeScopedLocal(STORAGE_KEYS[type], ownerKey, templates[type]);
      }
    });
  }
  if (messageOptions || billCompletionOptions) {
    const normalized = normalizeMessageOptions(messageOptions, billCompletionOptions);
    tenantCache.messageOptions = normalized;
    tenantCache.billCompletionOptions = normalized.billCompletion;
    writeScopedLocal(
      STORAGE_KEYS.messageOptions,
      ownerKey,
      JSON.stringify(normalized),
    );
    writeScopedLocal(
      STORAGE_KEYS.billCompletionOptions,
      ownerKey,
      JSON.stringify(normalized.billCompletion),
    );
  }
};

export default {
  refreshWhatsappConfig,
  getBusinessContext,
  resolveFrontendBaseUrl,
  buildCustomerLoginUrl,
  buildBillCompletionMessageData,
  loadTemplate,
  replacePlaceholders,
  generateMessage,
  generatePublicJobAlertMessage,
  generateBillCompletionMessage,
  openWhatsApp,
  shareOnWhatsApp,
  encodeMessageForWhatsApp,
  loadBillCompletionOptions,
  loadMessageOptions,
  normalizeMessageOptions,
  applyMessageOptions,
  saveTemplatesLocally,
  STORAGE_KEYS,
};
